'use client';

import { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { Comparison, ComparisonStatus, ComparisonStatistics } from "@/api/generated/client/models";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatCurrency } from "@/lib/utils";
import { apiClient } from "@/lib/api-client";

interface ComparisonSelectorProps {
    comparisons: Comparison[];
    selectedComparison: Comparison | null;
    onSelectComparison: (comparison: Comparison | null, stats: ComparisonStatistics | null) => void;
}

export function ComparisonSelector({ 
    comparisons, 
    selectedComparison, 
    onSelectComparison 
}: ComparisonSelectorProps) {
    const [comparisonStats, setComparisonStats] = useState<Record<number, ComparisonStatistics>>({});
    const [loadingStats, setLoadingStats] = useState(false);

    // Filter comparisons to only show completed ones - memoized to prevent unnecessary re-renders
    const eligibleComparisons = useMemo(
        () => comparisons.filter(c => c.status === ComparisonStatus.Completed),
        [comparisons]
    );

    // Fetch statistics for all eligible comparisons
    useEffect(() => {
        if (eligibleComparisons.length > 0) {
            const fetchStats = async () => {
                setLoadingStats(true);
                try {
                    const statsPromises = eligibleComparisons.map(comp =>
                        apiClient.comparisons.getComparisonStatistics({ id: comp.id })
                            .then(stats => ({ id: comp.id, stats }))
                            .catch(() => ({ id: comp.id, stats: null }))
                    );
                    
                    const results = await Promise.all(statsPromises);
                    const statsMap: Record<number, ComparisonStatistics> = {};
                    results.forEach(({ id, stats }) => {
                        if (stats) {
                            statsMap[id] = stats;
                        }
                    });
                    setComparisonStats(statsMap);
                } catch (error) {
                    console.error('Error fetching comparison statistics:', error);
                } finally {
                    setLoadingStats(false);
                }
            };

            fetchStats();
        }
    }, [eligibleComparisons]);

    if (eligibleComparisons.length === 0) {
        return (
            <Alert className="shrink-0">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                    No completed comparisons available. Please complete a comparison first before requesting a supplement.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <Label className="text-sm font-semibold">Select Comparison</Label>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 -mr-2">
                {eligibleComparisons.map((comparison) => {
                    const stats = comparisonStats[comparison.id];
                    const unmatchedCount = stats?.unmatchedContractorCount || 0;
                    const isSelected = selectedComparison?.id === comparison.id;
                    
                    return (
                        <Card
                            key={comparison.id}
                            className={cn(
                                "p-4 cursor-pointer transition-all border-l-4",
                                isSelected
                                    ? "border-l-primary border-gray-200 bg-primary/5 shadow-sm"
                                    : "border-l-transparent hover:border-l-gray-300 hover:bg-muted/50"
                            )}
                            onClick={() => onSelectComparison(comparison, stats || null)}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 flex-1">
                                    <div className={cn(
                                        "mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0",
                                        isSelected
                                            ? "border-primary"
                                            : "border-muted-foreground/50"
                                    )}>
                                        {isSelected && (
                                            <Check className="h-3 w-3 text-primary" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="font-medium text-sm">{comparison.name}</p>
                                        </div>
                                        <p className="text-xs text-muted-foreground mb-2">
                                            Created {format(new Date(comparison.createdAt), 'MMM d, yyyy')}
                                        </p>
                                        
                                        {loadingStats ? (
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                Loading stats...
                                            </div>
                                        ) : stats ? (
                                            <div className="bg-muted/50 rounded-md p-2 space-y-1">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-muted-foreground">Unmatched Items:</span>
                                                    <span className="font-semibold">{unmatchedCount}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-muted-foreground">Unmatched RCV:</span>
                                                    <span className="font-semibold">{formatCurrency((stats.unmatchedContractorTotalCents || 0) / 100)}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-xs text-muted-foreground italic">Stats unavailable</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}
